import axios from 'axios';
import { BASE_URL } from './api';
import { supervisorModeParams } from '@/utils/outreachSupervisorMode';

// Bulk-prepare drafts for a template + a set of companies. Renders only; sends nothing.
export async function prepareOutreachDrafts(payload, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/prepare/`,
        headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
        },
        data: {
            template_id: payload.templateId,
            company_ids: payload.companyIds,
        }
    }).then((response) => {
        return response;
    }).catch((error) => {
        throw error
    });
}

// The analyst's draft queue -- every draft sent from a mailbox they can send
// from, whoever created it. Optional filters:
// { campaign[], client[], email_template[], mailbox[], created_by[], status, page }.
// Array filters are serialized as repeated params (campaign=1&campaign=2) for the
// DRF ModelMultipleChoiceFilter backend.
export async function getOutreachDrafts(token, params = {}) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-messages/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: { ...params, ...supervisorModeParams() },
        paramsSerializer: (rawParams) => {
            const sp = new URLSearchParams()
            Object.entries(rawParams).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((item) => sp.append(key, item))
                } else if (value !== undefined && value !== null && value !== '') {
                    sp.append(key, value)
                }
            })
            return sp.toString()
        }
    }).then((response) => {
        return {
            results: response.data.results,
            count: response.data.count,
            next: response.data.next,
            prev: response.data.previous,
            page: response.data.page,
            pageSize: response.data.page_size,
            numPages: response.data.num_pages,
        };
    }).catch((error) => {
        throw error
    });
}

// The send-from mailboxes present in this user's queue, for the From filter:
// [{ email_address, count, can_send }]. Read off the drafts themselves, so it
// lists exactly what's filterable rather than every address the user could send
// from. can_send is false only for mailboxes a dev user can see but not send as.
export async function getOutreachDraftFromAddresses(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-messages/from-addresses/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: supervisorModeParams(),
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Status breakdown across the filtered draft set, ignoring the tab. Pass only
// the refine filters ({ campaign[], client[], email_template[] }) -- omit
// status_group so the counts span every tab. Returns
// { counts: {status: n}, total, stale_unsent }.
export async function getOutreachDraftStatusSummary(token, params = {}) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-messages/status_summary/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: { ...params, ...supervisorModeParams() },
        paramsSerializer: (rawParams) => {
            const sp = new URLSearchParams()
            Object.entries(rawParams).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach((item) => sp.append(key, item))
                } else if (value !== undefined && value !== null && value !== '') {
                    sp.append(key, value)
                }
            })
            return sp.toString()
        }
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function getOutreachDraft(draftId, token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-messages/${draftId}/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: supervisorModeParams(),
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// The Outlook on the web link for any message on a thread, looked up live:
// { url }. 404 when it's gone from Outlook, 403 when no mailbox of yours has it.
export async function getOutreachMessageOutlookLink(messageId, token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/outreach-messages/${messageId}/outlook-link/`,
        headers: {
            'Authorization': `Token ${token}`
        },
        params: supervisorModeParams(),
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Autosave the working copy: { final_body_html, subject, status }.
export async function updateOutreachDraft(draftId, data, token) {
    return axios({
        method: 'patch',
        url: `${BASE_URL}/outreach-messages/${draftId}/`,
        headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
        },
        data: data
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Generate grounded LLM suggestions for the [TOKEN] placeholders in the given body.
export async function generateOutreachSuggestions(draftId, body, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/${draftId}/suggest/`,
        headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
        },
        data: { body }
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Send one finished draft to its target contact.
export async function sendOutreachDraft(draftId, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/${draftId}/send/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Queue a draft to send automatically at a future time. scheduledSendAt must be
// an absolute ISO 8601 instant with an offset (e.g. new Date(...).toISOString())
// so the server stores the exact moment the analyst picked, timezone and all.
// Returns the updated (now scheduled) draft.
export async function scheduleOutreachDraft(draftId, token, scheduledSendAt) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/${draftId}/schedule/`,
        headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
        },
        data: { scheduled_send_at: scheduledSendAt },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Cancel a queued send, returning the draft to the analyst's queue. Returns the
// updated draft.
export async function unscheduleOutreachDraft(draftId, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/${draftId}/unschedule/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Start a fresh draft from the same company + template as an existing (e.g.
// discarded) draft. Returns the new (or refreshed open) draft.
export async function recreateOutreachDraft(draftId, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/${draftId}/recreate/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Create a follow-up draft that replies within a sent draft's thread. Returns
// the new draft (linked to the parent via in_reply_to). Pass templateId to use a
// different template for the follow-up; omit it to reuse the parent's.
// messageId: the email on draftId's thread being answered, when it isn't
// draftId itself. draftId stays the access gate. replyAll copies everyone else
// on that email (the backend works out who).
export async function prepareFollowUpOutreachDraft(draftId, token, templateId = null, messageId = null, replyAll = false) {
    const data = {}
    if (templateId) data.template_id = templateId
    if (messageId) data.message_id = messageId
    if (replyAll) data.reply_all = true
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/${draftId}/prepare_follow_up/`,
        headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
        },
        data,
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Create follow-up drafts for many sent drafts at once. templateId is optional
// (null = each follow-up reuses its own parent's template). Returns
// { created, skipped: [ids] }.
export async function bulkPrepareFollowUpOutreachDrafts(draftIds, token, templateId = null) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/bulk_prepare_follow_up/`,
        headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
        },
        data: { draft_ids: draftIds, ...(templateId ? { template_id: templateId } : {}) },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Discard many drafts at once. Already-sent/discarded ones are skipped server-side.
// Returns { discarded, skipped }.
export async function bulkDiscardOutreachDrafts(draftIds, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/bulk_discard/`,
        headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
        },
        data: { draft_ids: draftIds },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

export async function discardOutreachDraft(draftId, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/${draftId}/discard/`,
        headers: {
            'Authorization': `Token ${token}`
        },
    }).then((response) => {
        return response.data;
    }).catch((error) => {
        throw error
    });
}

// Attach a file to one draft. Adds to the template's standing attachments
// rather than replacing them. Returns the updated draft.
export async function addDraftAttachment(draftId, file, token) {
    const formData = new FormData()
    formData.append('file', file, file.name)
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/${draftId}/attachments/`,
        headers: { 'Authorization': `Token ${token}` },
        data: formData,
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Remove one of the draft's own attachments. Template attachments aren't
// reachable here -- they belong to the template, not this email.
export async function removeDraftAttachment(draftId, attachmentId, token) {
    return axios({
        method: 'delete',
        url: `${BASE_URL}/outreach-messages/${draftId}/attachments/${attachmentId}/`,
        headers: { 'Authorization': `Token ${token}` },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Start a template-free reply in this message's thread: the bespoke case, where
// the analyst answers what the prospect actually said. Returns the new draft.
export async function replyToOutreachDraft(draftId, token, messageId = null, replyAll = false) {
    const data = {}
    if (messageId) data.message_id = messageId
    if (replyAll) data.reply_all = true
    return axios({
        method: 'post',
        url: `${BASE_URL}/outreach-messages/${draftId}/reply/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data,
    }).then((response) => response.data)
    .catch((error) => { throw error });
}
